'use client'
// Types
import ClicableItem from '@/Components/hoc/ClickableItem'
import { Box, Logo } from '@/CommonComponents'
import UserButton from './UserButton'
// MUI Components
import MUIAppBar from '@mui/material/AppBar'
import MUIBox from '@mui/material/Box'
import MUIMenu from '@mui/material/Menu'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIToolbar from '@mui/material/Toolbar'
// Hooks
import * as React from 'react'
// Constants
import { COLORS } from '@/lib/constants/colors'

interface IMenuAction {
  label?: string
  onClick?: () => void
  disabled?: boolean
  customRender?: React.ReactNode
  isHidden?: boolean
}

export interface IAppBarProps {
  menuActions: IMenuAction[]
  position?: 'fixed' | 'absolute' | 'relative' | 'static' | 'sticky' | undefined
  sx?: React.CSSProperties
  onLogoClick?: () => void
}

// eslint-disable-next-line max-lines-per-function
export default function AppBar({ menuActions, position, sx, onLogoClick }: IAppBarProps) {
  const [isOpenMenu, setIsOpenMenu] = React.useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)
  const [buttonWidth, setButtonWidth] = React.useState<number | undefined>(undefined)
  const appBarRef = React.useRef<HTMLDivElement>(null)

  const toggleMenu = () => {
    setIsOpenMenu((isOpenMenu) => !isOpenMenu)
  }

  React.useEffect(() => {
    if (isOpenMenu && anchorRef.current) {
      setButtonWidth(anchorRef.current.offsetWidth)
    }
  }, [isOpenMenu])

  return (
    <MUIAppBar
      ref={appBarRef}
      position={position || 'static'}
      sx={{
        ...sx,
        backgroundColor: COLORS.BACKGROUND.DEFAULT,
        boxShadow: 'none',
        borderBottom: `1px solid ${COLORS.BACKGROUND.DARK}`,
      }}
    >
      <MUIToolbar>
        <MUIBox sx={{ flexGrow: 1, pl: 1, lineHeight: 0 }}>
          <ClicableItem onClick={onLogoClick}>
            <Logo color={COLORS.PRIMARY.DARK} width={100} />
          </ClicableItem>
        </MUIBox>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box ref={anchorRef} sx={{ width: 'fit-content' }}>
            <UserButton onClick={toggleMenu} />
          </Box>
          <MUIMenu
            elevation={0}
            id="menu-appbar"
            anchorEl={appBarRef.current}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            open={isOpenMenu}
            onClose={toggleMenu}
            slotProps={{
              paper: {
                sx: {
                  width: buttonWidth,
                  minWidth: buttonWidth,
                  border: `1px solid ${COLORS.BORDER.DEFAULT}`,
                  borderRadius: 0,
                  boxShadow: 'none',
                },
              },
            }}
          >
            {menuActions
              .filter(({ isHidden }) => !isHidden)
              .map(({ onClick, label, disabled, customRender }) => (
                <MUIMenuItem
                  key={crypto.randomUUID()}
                  onClick={onClick}
                  disabled={disabled}
                  sx={{
                    width: '100%',
                    backgroundColor: 'transparent !important',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  {label || customRender}
                </MUIMenuItem>
              ))}
          </MUIMenu>
        </Box>
      </MUIToolbar>
    </MUIAppBar>
  )
}
